import type { APIAgentPrompt } from '@visorpro/client/dist/types/APIAgentPrompt'
import type { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import type { FC } from 'react'
import { useCallback } from 'react'
import { MdMoreVert } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { TableDateCell } from '../table/table-date-cell'
import { TableRow } from '../table/table-row'
import { TableTextCell } from '../table/table-text-cell'
import { DefaultBadge, OrganizationChips } from './cells'

export interface AgentPromptRowProps {
    prompt: APIAgentPrompt
    now: DateTime
    onShowMenu(e: React.MouseEvent, prompt: APIAgentPrompt): void
    onAssign(prompt: APIAgentPrompt): void
}

export const AgentPromptRow: FC<AgentPromptRowProps> = observer(
    ({ prompt, now, onShowMenu, onAssign }) => {
        const navigate = useNavigate()

        const onMoreClick = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation()
                onShowMenu(e, prompt)
            },
            [onShowMenu, prompt],
        )

        const onOpen = useCallback(
            () => navigate(`/prompts/${prompt.id}`),
            [navigate, prompt.id],
        )

        const onAssignClick = useCallback(
            () => onAssign(prompt),
            [onAssign, prompt],
        )

        return (
            <TableRow
                data-testid="agent-prompt-row"
                data-prompt-name={prompt.name}>
                <TableTextCell onClick={onOpen} style={{ cursor: 'pointer' }}>
                    {prompt.name}
                </TableTextCell>

                {/* The whole cell, so a prompt with no organizations yet is
                    still a target — that is the one you most want to click. */}
                <TableTextCell
                    data-testid="agent-prompt-orgs-cell"
                    title="Assign organizations"
                    style={{ cursor: 'pointer' }}
                    onClick={onAssignClick}>
                    <OrganizationChips organizations={prompt.organizations} />
                </TableTextCell>

                <TableTextCell>
                    <DefaultBadge isDefault={prompt.is_default} />
                </TableTextCell>

                <TableDateCell date={prompt.updated_at} now={now} />

                <td className="table-cell">
                    <MdMoreVert
                        size={18}
                        data-testid="agent-prompt-menu"
                        onClick={onMoreClick}
                        className="dimmed-pressable-icon"
                    />
                </td>
            </TableRow>
        )
    },
)
